import React, { useMemo, useState, useEffect } from 'react';
import { logger } from '../../utils/logger';
import { libraryCacheManager } from '../../utils/libraryCache';

// Type definition for QR code library
interface QRCodeModule {
  QRCodeSVG: React.ComponentType<QRCodeProps>;
  QRCodeCanvas?: React.ComponentType<QRCodeProps>;
}

interface QRCodeProps {
  value: string;
  size?: number;
  level?: 'L' | 'M' | 'Q' | 'H';
  includeMargin?: boolean;
  className?: string;
  style?: React.CSSProperties;
  bgColor?: string;
  fgColor?: string;
  imageSettings?: {
    src: string;
    height: number;
    width: number;
    excavate: boolean;
  };
  [key: string]: unknown;
}

interface OptimizedQRCodeProps {
  value: string;
  size?: number;
  level?: 'L' | 'M' | 'Q' | 'H';
  includeMargin?: boolean;
  className?: string;
  style?: React.CSSProperties;
  bgColor?: string;
  fgColor?: string;
  imageSettings?: {
    src: string;
    height: number;
    width: number;
    excavate: boolean;
  };
  lazy?: boolean;
  cacheKey?: string;
  onGenerated?: () => void;
  priority?: 'high' | 'low';
}

const OptimizedQRCode: React.FC<OptimizedQRCodeProps> = ({
  value,
  size = 256,
  level = 'M',
  includeMargin = false,
  className,
  style,
  bgColor = '#FFFFFF',
  fgColor = '#000000',
  imageSettings,
  lazy = false,
  cacheKey,
  onGenerated,
  priority = 'low',
  ...props
}) => {
  const [QRComponent, setQRComponent] = useState<React.ComponentType<QRCodeProps> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldLoad, setShouldLoad] = useState(!lazy || priority === 'high');

  // Memoize cache key
  const memoizedCacheKey = useMemo(() => {
    if (cacheKey) return cacheKey;
    return `${value}_${size}_${level}_${bgColor}_${fgColor}_${includeMargin}`;
  }, [cacheKey, value, size, level, bgColor, fgColor, includeMargin]);

  // Memoize QR code props to prevent unnecessary re-renders
  const qrProps = useMemo(
    () => ({
      value,
      size,
      level,
      includeMargin,
      className,
      style,
      bgColor,
      fgColor,
      imageSettings,
      ...props,
    }),
    [value, size, level, includeMargin, className, style, bgColor, fgColor, imageSettings, props],
  );

  // Load QR code library with cache support and retry mechanism
  useEffect(() => {
    const loadQRCode = async (retryCount = 0) => {
      if (!shouldLoad || QRComponent) return;

      try {
        setIsLoading(true);

        // Add delay for low priority QR codes
        if (priority === 'low') {
          await new Promise((resolve) => setTimeout(resolve, 150));
        }

        // Try to get from cache first, then fallback to dynamic import
        const qrModule = (await libraryCacheManager.getQRCodeLibrary()) as QRCodeModule;
        setQRComponent(() => qrModule.QRCodeSVG);

        if (onGenerated) {
          onGenerated();
        }
      } catch (error) {
        logger.error('Failed to load QR Code library:', error);

        // Retry loading if failed and retryCount < 2
        if (retryCount < 2) {
          setTimeout(
            () => {
              logger.warn(`Retrying QR code library loading... (${retryCount + 1}/2)`);
              loadQRCode(retryCount + 1);
            },
            1000 * (retryCount + 1),
          ); // Exponential backoff
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadQRCode();
  }, [shouldLoad, QRComponent, priority, onGenerated]);

  // Handle lazy loading trigger
  useEffect(() => {
    if (lazy && !shouldLoad) {
      // Use intersection observer or other trigger
      const timer = setTimeout(() => {
        setShouldLoad(true);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [lazy, shouldLoad]);

  // Render loading placeholder
  if (isLoading || !QRComponent) {
    return (
      <div
        className={`${className} bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center transition-all duration-300 hover:border-gray-400`}
        style={{
          width: size,
          height: size,
          ...style,
        }}
        aria-label="Loading QR code"
      >
        <div className="text-center p-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
          <div className="text-gray-600 text-xs font-medium mb-1">
            {isLoading ? 'Generating QR Code...' : 'QR Ready'}
          </div>
          <div className="text-gray-400 text-xs">
            {priority === 'high' ? 'Loading...' : 'Please wait'}
          </div>
        </div>
      </div>
    );
  }

  // Render the actual QR code
  return (
    <div className="qr-code-container" data-cache-key={memoizedCacheKey}>
      <QRComponent {...qrProps} />
    </div>
  );
};

export default React.memo(OptimizedQRCode);
